import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import TransactionDetails from './transactionDetails';
import { useLocation } from 'react-router-dom';
import { usePage } from '../../../context/PageContext';
import { ChevronDown, Delete, LinearCloseCirlce } from '../../icons';
import { useTransaction } from '../../../context/TransactionContext';
import ConfirmModal from '../../confirmationmodal/confirmationModal';
import { useDeleteTransaction } from '../../../hooks/useDeleteTransaction';
import { TransactionStatus, TransactionViewTab } from '../../../constants/transaction';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import Notes from '../../conflictSearch/notes';
import { NotesEntity } from '../../../constants/notes';
import AddEditTransactionHeader from '../addTransaction/addEditTransactionHeader';
import { AnalyticsPage } from '../../../interface/analytics';
import { useAnalytics } from '../../../hooks/useAnalytics';
import CustomTabs from '../../customTabs/customTabs';
import { IAPIError, TransactionException } from '../../../interface/common';
import CustomModal from '../../informationPopup/customModal';
import { useEmailCategoryTagger } from '../../../hooks/useEmailCategoryTagger';
import { useUser } from '../../../context/UserContext';
import { UserType } from '../../../constants/constants';
import DetailScrollArea from '../../detailScrollArea/detailScrollArea';

const TransactionView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const fromTab = Object.values(TransactionViewTab).includes(location?.state?.fromTab)
    ? location.state.fromTab
    : TransactionViewTab.general;

  const { setTitle } = usePage();
  const { trackPage } = useAnalytics();
  const { user } = useUser();
  const { goBack, updateTop } = useNavigationStack();
  const { transaction, updateTransaction } = useTransaction();
  const { untagEmailCategory } = useEmailCategoryTagger();
  const deleteTransactionMutation = useDeleteTransaction();
  const [selectedTab, setSelectedTab] = useState<string>(fromTab.toString());
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [isDeleteError, setIsDeleteError] = useState<boolean>(false);
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>('');

  useEffect(() => {
    updateTop({ fromTab: selectedTab });
  }, [selectedTab]);

  const allowedDeleteStatuses = [
    TransactionStatus.open,
    TransactionStatus.nonBillable,
    TransactionStatus.reviewed,
  ];
  const isDeleteAllowed =
    (user?.userType === UserType.ADMIN || transaction?.transactionUser?.id === user?.id) &&
    allowedDeleteStatuses.includes(transaction?.status as TransactionStatus);

  useEffect(() => {
    trackPage(AnalyticsPage.TRANSACTION_DETAIL);
  }, [trackPage]);

  const items = useMemo(
    () => [
      {
        key: String(TransactionViewTab.general),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <TransactionDetails />
          </DetailScrollArea>
        ),
      },
      {
        key: String(TransactionViewTab.notes),
        label: t('common:tab.notes'),
        children: (
          <Notes entity={NotesEntity.TRANSACTION} entitySubject={transaction?.subject ?? ''} />
        ),
      },
    ],
    [transaction],
  );

  const handleConfirmDelete = () => {
    setIsDeleteModalVisible(true);
  };
  const handleDeleteTransaction = () => {
    deleteTransactionMutation.mutate(transaction?.id ?? '', {
      async onSuccess() {
        const categoryName = `[CSI - ${t('transaction:transaction')}]`;
        await untagEmailCategory(categoryName, { transactionId: '' });
        handleBack();
      },
      onSettled() {
        setIsDeleteModalVisible(false);
      },
      onError: (error) => {
        const responseData = error.response?.data as IAPIError;
        if (responseData?.type === TransactionException.TransactionCantBeRemovedException) {
          setDeleteErrorMessage(t('transaction:error.transactionCantBeRemovedException'));
          setIsDeleteError(true);
        }
      },
    });
  };
  const handleBack = () => {
    updateTransaction(null);
    goBack();
  };
  const renderHeader = () => {
    return (
      <AddEditTransactionHeader
        handleDelete={isDeleteAllowed ? handleConfirmDelete : undefined}
        handleBack={handleBack}
        title={transaction?.subject ?? ''}
        subTitle={t('transaction:transaction')}
      />
    );
  };
  useEffect(() => {
    if (transaction && Object.keys(transaction).length > 0) {
      setTitle(renderHeader());
    }
  }, [transaction]);
  const ErrorModal = useMemo(
    () => (
      <CustomModal
        visible={isDeleteError}
        icon={<LinearCloseCirlce />}
        confirmText={t('common:ok')}
        onConfirm={() => setIsDeleteError(false)}
        title={deleteErrorMessage}
      />
    ),
    [isDeleteError, deleteErrorMessage],
  );

  return (
    <>
      <CustomTabs
        variant="pill"
        activeKey={selectedTab}
        onChange={(key) => setSelectedTab(key)}
        items={items}
        moreIcon={
          // antd gives the overflow button no name of its own, so it comes from the icon.
          <span role="img" aria-label={t('common:showMore')}>
            <ChevronDown />
          </span>
        }
      />
      <ConfirmModal
        visible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        icon={<Delete />}
        title={t('transaction:deleteTransactionQuestion')}
        subtitle={t('transaction:deleteTransactionText')}
        cancelText={t('common:cancel')}
        confirmText={t('transaction:delete')}
        onConfirm={handleDeleteTransaction}
      />
      {ErrorModal}
    </>
  );
};

export default TransactionView;
